import { useNavigation, useNavigationType } from "@remix-run/react"
import { useEffect, useRef } from "react"

// Survives layout remounts (unlike a ref), so it marks the initial document
// load rather than the first mount of a layout. Any POP into a layout after
// this is set is a client-side Back/Forward and should still move focus.
let hasRunInDocument = false

// After a client-side navigation, move keyboard focus to the returned element
// so keyboard and screen reader users know a new page has loaded (WCAG 2.4.3).
// Skipped on the initial page load, and when the destination moved focus into
// the target itself (e.g. a child focused its own control). Focus that merely
// survived from the source page (a link kept mounted across same-layout
// routes) is still reset.
const useFocusOnRouteChange = <T extends HTMLElement>(pathname: string) => {
  const ref = useRef<T>(null)
  const navigationType = useNavigationType()
  const navigationState = useNavigation().state
  const previousPathname = useRef<string | null>(null)
  const sourceFocus = useRef<Element | null>(null)

  useEffect(() => {
    if (navigationState !== "idle") sourceFocus.current = document.activeElement
  }, [navigationState])

  useEffect(() => {
    const isInitialLoad = !hasRunInDocument && navigationType === "POP"
    hasRunInDocument = true
    const previous = previousPathname.current
    previousPathname.current = pathname
    if (previous === null ? isInitialLoad : previous === pathname) return

    const source = sourceFocus.current
    sourceFocus.current = null
    const target = ref.current
    const active = document.activeElement
    if (!target || (active !== source && target.contains(active))) return
    target.focus({ preventScroll: true })
  }, [pathname, navigationType])

  return ref
}

export default useFocusOnRouteChange
